/**
 * Dynamic page title hook - sets document.title based on current module and optional page name.
 *
 * Title format:
 * - With page title: "{Page} - {Module} | PanLuma"
 * - Module only:     "{Module} | PanLuma"
 * - Home module:     "PanLuma"
 *
 * Safe to call outside ModuleProvider (no-ops gracefully).
 *
 * The module segment is the PERSONA-AWARE display name ("Freight Operations",
 * not "Shipments"), matching the sidebar, header, breadcrumbs and module picker.
 * It is read from `ModuleContext.currentModuleName`, which `ModuleProvider`
 * resolves once via `useModuleName`. usePageTitle deliberately does NOT call
 * usePersona itself: it is consumed by PageHeader on virtually every page, so a
 * direct usePersona (→ useAuth + react-query) dependency would force those
 * providers on every test that renders a page header.
 */

import { useContext, useEffect } from 'react';
import { ModuleContext } from '../contexts/ModuleContext';

const APP_NAME = 'PanLuma';

/**
 * Sets the browser tab title based on the current module and an optional page title.
 *
 * @param pageTitle - The page name to display (already translated/resolved string).
 *   If omitted, only the module name is shown.
 *
 * Usage:
 *   usePageTitle('Candidates');  // → "Candidates - Recruiting | PanLuma"
 *   usePageTitle();              // → "Recruiting | PanLuma"
 */
export function usePageTitle(pageTitle?: string) {
  const moduleContext = useContext(ModuleContext);

  useEffect(() => {
    // No-op when outside ModuleProvider (e.g., in tests or login pages)
    if (!moduleContext) return;

    const { currentModule, currentModuleName } = moduleContext;
    const isHome = currentModule.id === 'home';

    if (isHome) {
      document.title = APP_NAME;
    } else if (pageTitle) {
      document.title = `${pageTitle} - ${currentModuleName} | ${APP_NAME}`;
    } else {
      document.title = `${currentModuleName} | ${APP_NAME}`;
    }
  }, [pageTitle, moduleContext]);
}
